import React, { useEffect, useRef, useState } from "react";
import { X, Settings, Image as ImageIcon, Trash2, Crop, Check, Globe, Clock, Calendar, RefreshCw, ChevronDown } from "lucide-react";
import { useWorkspace } from "../context/WorkspaceContext";
import { useSettings } from "../context/SettingsContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { confirmDelete } from "./ConfirmDialog";
import CalendarImportModal from "./CalendarImportModal";
import { WallpaperCropper } from "./CalendarSettings";

// ─── persist keys scoped per workspace ────────────────────────────────────────
const wsKey = (wsId, k) => `crescendo:ws:${wsId}:${k}`;


// ─── Recent backgrounds (stored as array of {url, dim, blur} per user) ────────
const RECENT_BG_KEY = (uid) => uid ? `crescendo:u:${uid}:recentBgs` : "crescendo:recentBgs";
const MAX_RECENT_BGS = 6;

function getRecentBgs(uid) {
  try {
    return JSON.parse(localStorage.getItem(RECENT_BG_KEY(uid)) || "[]");
  } catch { return []; }
}

function addRecentBg(uid, url, dim, blur, sidebarDim, envBlur) {
  if (!isValidBgUrl(url)) return;
  const key = RECENT_BG_KEY(uid);
  let recents = getRecentBgs(uid);
  // Remove if already exists (deduplicate by URL)
  recents = recents.filter(r => r.url !== url);
  recents.unshift({ url, dim, blur, sidebarDim, envBlur, ts: Date.now() });
  recents = recents.slice(0, MAX_RECENT_BGS);
  try { localStorage.setItem(key, JSON.stringify(recents)); } catch {}
}



// Only accept valid image URLs — reject "undefined", "null", or empty
function isValidBgUrl(url) {
  if (!url || url === "undefined" || url === "null" || url.trim() === "") return false;
  return url.startsWith("http") || url.startsWith("data:image") || url.startsWith("/");
}

function _setBg(url, dim, blur, at, sidebarDim = 0.82, envBlur = 0) {
  const body = document.body;
  const root = document.documentElement;
  console.log("[crescendo:bg] _setBg called", { urlLen: url?.length, dim, blur, at });

  if (url) {
    const d = Math.max(0, Math.min(0.95, dim));
    body.style.backgroundImage      = `linear-gradient(rgba(0,0,0,${d}),rgba(0,0,0,${d})),url("${url}")`;
    body.style.backgroundSize       = "cover";
    body.style.backgroundPosition   = "center";
    body.style.backgroundAttachment = "fixed";
    body.style.backgroundRepeat     = "no-repeat";
    body.style.backgroundColor      = "transparent";

    // CSS custom properties — cascade instantly, no React re-render required
    root.style.setProperty("--cresc-panel-bg",   "transparent");
    root.style.setProperty("--cresc-sidebar-bg", `rgba(12,12,12,${sidebarDim})`);
    root.style.setProperty("--cresc-struct-bg",  `rgba(12,12,12,${Math.min(sidebarDim, 0.72)})`);
    root.style.setProperty("--cresc-topbar-bg",  `rgba(12,12,12,${Math.min(sidebarDim, 0.72)})`);
    root.style.setProperty("--cresc-sidebar-dim", String(sidebarDim));
    root.style.setProperty("--cresc-bg-blur",    `${Math.max(0, blur)}px`);
    root.style.setProperty("--cresc-env-blur",    `${Math.max(0, envBlur ?? 0)}px`);
    console.log("[crescendo:bg] body.style + CSS vars set, bgImage length:", body.style.backgroundImage.length);
  } else {
    body.style.backgroundImage = body.style.backgroundSize = body.style.backgroundPosition = "";
    body.style.backgroundAttachment = body.style.backgroundRepeat = body.style.backgroundColor = "";
    root.style.removeProperty("--cresc-panel-bg");
    root.style.removeProperty("--cresc-sidebar-bg");
    root.style.removeProperty("--cresc-struct-bg");
    root.style.removeProperty("--cresc-topbar-bg");
    root.style.removeProperty("--cresc-bg-blur");
    root.style.removeProperty("--cresc-env-blur");
    const s = document.getElementById("cresc-wallpaper-style");
    if (s) s.textContent = "";
  }

  body.classList.toggle("cresc-has-bg",       !!url);
  body.classList.toggle("cresc-bg-calendar",  !!url && at !== "documents");
  body.classList.toggle("cresc-bg-documents", !!url && at !== "calendar");
  console.log("[crescendo:bg] body classes:", body.className);

  // Direct DOM override — inline !important beats any CSS rule
  _applyDomStyles(!!url);
  // Re-apply after React re-renders (handles elements rendered after this call)
  requestAnimationFrame(() => _applyDomStyles(!!url));
  setTimeout(() => _applyDomStyles(!!url), 200);

  window.dispatchEvent(new CustomEvent("cresc-bg-changed", { detail: { url, dim, blur } }));
}

function _applyDomStyles(enable) {
  const sd = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue("--cresc-sidebar-dim").trim() || "0.82"
  );
  const transparent = "transparent";
  const frosted     = `rgba(12,12,12,${sd})`;
  const struct      = `rgba(12,12,12,${Math.min(sd, 0.72)})`;

  // Respect applyTo — only make the relevant view transparent
  const body = document.body;
  const calOnly = enable && body.classList.contains("cresc-bg-calendar") && !body.classList.contains("cresc-bg-documents");
  const docsOnly = enable && body.classList.contains("cresc-bg-documents") && !body.classList.contains("cresc-bg-calendar");

  // When calOnly: don't apply transparency to docs-root (leave it opaque)
  // When docsOnly: don't apply transparency to cal-root/canvas/day-col-bg
  const transparentSels = [
    "[data-app-root]",
    "[data-app-shell]","[data-panel-group]","[data-panel]",
    "[data-cresc-mainpanel]",
    ...(docsOnly ? [] : ["[data-cresc-cal-root]","[data-cresc-cal-canvas]",".day-col-bg"]),
    ...(calOnly  ? [] : ["[data-cresc-docs-root]"]),
  ];
  const frostedSels  = ["[data-cresc-sidebar]","[data-cresc-rightpanel]"];
  const structSels   = [
    "[data-cresc-col-header]","[data-testid='all-day-strip']",
    "[data-testid='capacity-bar']","[data-testid='pinned-bar']",
    "[data-cresc-topbar]","[data-testid='workspace-header']",
    ".keep-dark",
  ];

  const envB = getComputedStyle(document.documentElement).getPropertyValue("--cresc-env-blur").trim() || "0px";

  if (enable) {
    transparentSels.forEach(s => document.querySelectorAll(s).forEach(el => {
      el.style.setProperty("background", transparent, "important");
    }));
    // env blur on main content area
    ["[data-cresc-mainpanel]","[data-cresc-cal-root]","[data-cresc-docs-root]"].forEach(s =>
      document.querySelectorAll(s).forEach(el => {
        if (envB && envB !== "0px") {
          el.style.setProperty("backdrop-filter", `blur(${envB}) saturate(1.1)`, "important");
          el.style.setProperty("-webkit-backdrop-filter", `blur(${envB}) saturate(1.1)`, "important");
        } else {
          el.style.removeProperty("backdrop-filter");
          el.style.removeProperty("-webkit-backdrop-filter");
        }
      })
    );
    frostedSels.forEach(s => document.querySelectorAll(s).forEach(el =>
      el.style.setProperty("background", frosted, "important")));
    structSels.forEach(s => document.querySelectorAll(s).forEach(el =>
      el.style.setProperty("background", struct, "important")));
  } else {
    [...transparentSels, ...frostedSels, ...structSels].forEach(s =>
      document.querySelectorAll(s).forEach(el =>
        el.style.removeProperty("background")));
  }
}

export function _applyBg(url, dim, blur, applyTo, sidebarDim, envBlur) {
  window._crescendoApplyDom = _applyDomStyles;
  const at = applyTo || localStorage.getItem("crescendo:bgApplyTo") || "everywhere";
  const sd = sidebarDim ?? parseFloat(localStorage.getItem("crescendo:sidebarDim") || "0.25");
  const eb = envBlur   ?? 0;
  if (!isValidBgUrl(url)) { _setBg("", dim, blur, at, sd, eb); return; }
  _setBg(url, dim, blur, at, sd, eb);
}

export function applyWorkspaceBgVars(wsId) {
  const url  = localStorage.getItem(wsKey(wsId, "bgUrl"))  || localStorage.getItem("crescendo:calBgUrl") || "";
  const dim  = parseFloat(localStorage.getItem(wsKey(wsId, "bgDim"))  || localStorage.getItem("crescendo:calBgDim")  || "0.35");
  const blur = parseFloat(localStorage.getItem(wsKey(wsId, "bgBlur")) || localStorage.getItem("crescendo:calBgBlur") || "12");
  const sd = parseFloat(localStorage.getItem("crescendo:sidebarDim") || "0.82");
  _applyBg(url, dim, blur, undefined, sd);
}

export function applyCalendarBgVars(userId) {
  const prefix = userId ? `crescendo:u:${userId}` : "crescendo";
  const url  = localStorage.getItem(`${prefix}:bgUrl`)  || "";
  const dim  = parseFloat(localStorage.getItem(`${prefix}:bgDim`)  || "0.80");
  const blur = parseFloat(localStorage.getItem(`${prefix}:bgBlur`) || "12");
  const sd   = parseFloat(localStorage.getItem(`${prefix}:sidebarDim`) || "0.25");
  const eb   = parseFloat(localStorage.getItem(`${prefix}:envBlur`) || "0");
  _applyBg(url, dim, blur, undefined, sd, eb);
}

export function clearWallpaper() {
  _applyBg("", 0.35, 12, undefined, 0.82);
}

export async function syncBackgroundFromServer(api, userId) {
  try {
    const prefix = userId ? `crescendo:u:${userId}` : "crescendo";
    // Server is authoritative — always fetch, apply server data if present
    const { data } = await api.get("/me/background");
    if (data?.url && isValidBgUrl(data.url)) {
      const sd = parseFloat(localStorage.getItem(`${prefix}:sidebarDim`) || "0.82");
      localStorage.setItem(`${prefix}:bgUrl`,  data.url);
      localStorage.setItem(`${prefix}:bgDim`,  String(data.dim  ?? 0.35));
      localStorage.setItem(`${prefix}:bgBlur`, String(data.blur ?? 12));
      _applyBg(data.url, data.dim ?? 0.35, data.blur ?? 12, undefined, sd);
    } else {
      // No server wallpaper — apply from user-scoped localStorage (this device only)
      applyCalendarBgVars(userId);
    }
  } catch {
    // Network error — fall back to user-scoped localStorage
    applyCalendarBgVars(userId);
  }
}

export async function uploadBackgroundToServer(api, url, dim, blur) {
  try { await api.put("/me/background", { url, dim: dim ?? 0.35, blur: blur ?? 12 }); } catch {}
}


// ── DayHeaderEditor ──────────────────────────────────────────────────────────
function DayHeaderEditor() {
  const read = (k, def) => { try { return localStorage.getItem(k) || def; } catch { return def; } };
  const write = (k, v) => { try { localStorage.setItem(k, v); window.dispatchEvent(new Event("crescendo:day-fmt-changed")); } catch {} };
  const [order, setOrder]       = React.useState(() => read("crescendo:dayHeaderOrder", "day-first"));
  const [fmt, setFmt]           = React.useState(() => read("crescendo:dayFmt", "short"));
  const [daySize, setDaySize]   = React.useState(() => parseInt(read("crescendo:dayNameSize", "9")));
  const [dateSize, setDateSize] = React.useState(() => parseInt(read("crescendo:dateNumSize", "14")));
  const [dragOver, setDragOver] = React.useState(null);

  const apply = (o, f, ds, ns) => {
    write("crescendo:dayHeaderOrder", o); write("crescendo:dayFmt", f);
    write("crescendo:dayNameSize", String(ds)); write("crescendo:dateNumSize", String(ns));
    setOrder(o); setFmt(f); setDaySize(ds); setDateSize(ns);
  };
  const dayLabel = fmt === "expanded" ? "Monday" : fmt === "minimal" ? "M" : "Mon";

  const dayEl = (
    <div draggable onDragStart={e => e.dataTransfer.setData("text/plain","day")}
      onDragOver={e => { e.preventDefault(); setDragOver("day"); }}
      onDrop={e => { e.preventDefault(); setDragOver(null); if(e.dataTransfer.getData("text/plain")==="date") apply("date-first",fmt,daySize,dateSize); }}
      onDragLeave={() => setDragOver(null)}
      className={`cursor-grab select-none px-1 rounded transition-colors ${dragOver==="day"?"bg-[#5A90F5]/20":""}`}
      style={{ fontSize:daySize, color:"#6B7280", fontWeight:500, letterSpacing:"0.08em", textTransform:"uppercase" }}>
      {dayLabel}
    </div>
  );
  const dateEl = (
    <div draggable onDragStart={e => e.dataTransfer.setData("text/plain","date")}
      onDragOver={e => { e.preventDefault(); setDragOver("date"); }}
      onDrop={e => { e.preventDefault(); setDragOver(null); if(e.dataTransfer.getData("text/plain")==="day") apply("day-first",fmt,daySize,dateSize); }}
      onDragLeave={() => setDragOver(null)}
      className={`cursor-grab select-none px-1 rounded transition-colors ${dragOver==="date"?"bg-[#5A90F5]/20":""}`}
      style={{ fontSize:dateSize, color:"#A1A1AA", fontWeight:600 }}>
      19
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg p-3">
        <div className="text-[9px] text-[#3F3F46] uppercase tracking-wider mb-2">Preview — drag to reorder</div>
        <div className="bg-[#141414] border border-[#1E1E1E] rounded h-14 flex items-center justify-center">
          <div className="flex flex-col items-center gap-0.5">
            {order==="day-first" ? <>{dayEl}{dateEl}</> : <>{dateEl}{dayEl}</>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[10px] text-[#A1A1AA] w-20 flex-shrink-0">Day name</span>
        <div className="flex gap-1 flex-1">
          {[["expanded","Full"],["short","Short"],["minimal","Min"]].map(([k,l]) => (
            <button key={k} onClick={() => apply(order,k,daySize,dateSize)}
              className={`flex-1 py-1 rounded border text-[10px] transition-colors ${fmt===k?"border-[#5A90F5] bg-[#5A90F5]/10 text-[#5A90F5]":"border-[#2A2A2A] text-[#52525B] hover:border-[#3A3A3A]"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => apply(order,fmt,Math.max(7,daySize-1),dateSize)} className="w-5 h-5 rounded border border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA] text-[10px] flex items-center justify-center">−</button>
          <span className="text-[10px] text-[#A1A1AA] w-5 text-center">{daySize}</span>
          <button onClick={() => apply(order,fmt,Math.min(16,daySize+1),dateSize)} className="w-5 h-5 rounded border border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA] text-[10px] flex items-center justify-center">+</button>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[10px] text-[#A1A1AA] w-20 flex-shrink-0">Date number</span>
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          <button onClick={() => apply(order,fmt,daySize,Math.max(9,dateSize-1))} className="w-5 h-5 rounded border border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA] text-[10px] flex items-center justify-center">−</button>
          <span className="text-[10px] text-[#A1A1AA] w-5 text-center">{dateSize}</span>
          <button onClick={() => apply(order,fmt,daySize,Math.min(24,dateSize+1))} className="w-5 h-5 rounded border border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA] text-[10px] flex items-center justify-center">+</button>
        </div>
      </div>
    </div>
  );
}


export function WorkspaceSettingsButton({ onOpenImport }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]"
        title="Workspace Settings"
        data-testid="workspace-settings-btn"
      >
        <Settings className="w-4 h-4" />
      </button>
      {open && <WorkspaceSettingsModal onClose={() => setOpen(false)} onOpenImport={onOpenImport} />}
    </>
  );
}

// Keep old export name so nothing breaks
export function CalendarSettingsButton({ onOpenImport }) {
  return <WorkspaceSettingsButton onOpenImport={onOpenImport} />;
}

const COLOR_PRESETS = ["#5A90F5","#0055FF","#A855F7","#14B8A6","#00C781","#FFCC00","#FF8A00","#FF3B30","#6366F1","#EC4899","#F97316","#84CC16","#06B6D4","#8B5CF6","#EF4444","#10B981"];
const TIMEZONES = Intl.supportedValuesOf ? Intl.supportedValuesOf("timeZone") : ["UTC","America/New_York","America/Chicago","America/Denver","America/Los_Angeles","Europe/London","Europe/Paris","Asia/Tokyo","Asia/Shanghai","Australia/Sydney"];

function WorkspaceSettingsModal({ onClose, onOpenImport }) {
  const { current, setWorkspaces, workspaces } = useWorkspace();
  const { clock, setClock, weekStartsOn, setWeekStartsOn, weekHours, setWeekHours, weekdayTints, setWeekdayTints } = useSettings();
  const wsId = current?.workspace_id;
  const [activeSettingsTab, setActiveSettingsTab] = useState("workspace");

  // Workspace identity
  const [wsName,  setWsName]  = useState(current?.name        || "");
  const [wsColor, setWsColor] = useState(current?.color       || "#5A90F5");
  const [wsDesc,  setWsDesc]  = useState(current?.description || "");
  const [savingWs, setSavingWs] = useState(false);

  // Wallpaper
  const uid = current?.user_id;
  const bgPrefix = uid ? `crescendo:u:${uid}` : "crescendo";
  const [bgUrl,      setBgUrl]      = useState(() => localStorage.getItem(wsKey(wsId, "bgUrl")) || localStorage.getItem(`${bgPrefix}:bgUrl`) || "");
  const [dim,        setDim]        = useState(() => parseFloat(localStorage.getItem(wsKey(wsId, "bgDim")) || localStorage.getItem(`${bgPrefix}:bgDim`) || "0.80"));
  const [bgBlur,     setBgBlur]     = useState(() => parseFloat(localStorage.getItem(wsKey(wsId, "bgBlur")) || localStorage.getItem(`${bgPrefix}:bgBlur`) || "12"));
  const [envBlur,    setEnvBlur]    = useState(() => parseFloat(localStorage.getItem(`${bgPrefix}:envBlur`) || "0"));
  const [sidebarDim, setSidebarDim] = useState(() => parseFloat(localStorage.getItem(`${bgPrefix}:sidebarDim`) || "0.25"));
  const [recentBgs, setRecentBgs] = useState(() => getRecentBgs(current?.user_id));

  // Where the wallpaper applies: "everywhere" | "calendar" | "documents" | "off"
  const [bgApplyTo, setBgApplyTo] = useState(() => localStorage.getItem("crescendo:bgApplyTo") || "everywhere");
  const [cropSrc,  setCropSrc]  = useState(null);

  // Display
  const [zoom, setZoom] = useState(() => parseFloat(localStorage.getItem(`crescendo:ws:${wsId}:zoom`) || localStorage.getItem("crescendo:colZoom") || "1") || 1);
  const [timezone, setTimezone] = useState(() => localStorage.getItem(`crescendo:ws:${wsId}:tz`) || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");

  // Danger
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 32 * 1024 * 1024) { toast.error("Image must be under 32 MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setCropSrc(String(ev.target.result || ""));
    reader.readAsDataURL(file);
  };

  const saveWallpaper = () => {
    const uid = current?.user_id;
    const prefix = uid ? `crescendo:u:${uid}` : "crescendo";
    try {
      if (wsId) {
        localStorage.setItem(wsKey(wsId, "bgUrl"),  bgUrl);
        localStorage.setItem(wsKey(wsId, "bgDim"),  String(dim));
        localStorage.setItem(wsKey(wsId, "bgBlur"), String(bgBlur));
      }
      localStorage.setItem(`${prefix}:bgUrl`,       bgUrl);
      localStorage.setItem(`${prefix}:bgDim`,       String(dim));
      localStorage.setItem(`${prefix}:bgBlur`,      String(bgBlur));
      localStorage.setItem(`${prefix}:sidebarDim`,  String(sidebarDim));
      localStorage.setItem(`${prefix}:envBlur`,     String(envBlur));
      localStorage.setItem(`${prefix}:bgApplyTo`,   bgApplyTo);
    } catch (e) {
      console.warn("[crescendo] localStorage quota exceeded:", e.message);
      toast.error("Image too large to save persistently — applied for this session only");
    }
    // Update recents (now labeled "Presets" in UI)
    if (isValidBgUrl(bgUrl)) {
      addRecentBg(current?.user_id, bgUrl, dim, bgBlur, sidebarDim, envBlur);
      setRecentBgs(getRecentBgs(current?.user_id));
    }
    _applyBg(bgUrl, dim, bgBlur, undefined, sidebarDim, envBlur);
    if (wsId) uploadBackgroundToServer(api, bgUrl, dim, bgBlur).catch(() => {});
  };

  const saveDisplay = () => {
    if (!wsId) return;
    localStorage.setItem(`crescendo:ws:${wsId}:zoom`, String(zoom));
    localStorage.setItem("crescendo:colZoom", String(zoom));
    localStorage.setItem(`crescendo:ws:${wsId}:tz`, timezone);
    window.dispatchEvent(new CustomEvent("crescendo:calsettings-changed"));
  };

  const saveWorkspaceIdentity = async () => {
    if (!wsId) return;
    setSavingWs(true);
    try {
      const { data } = await api.put(`/workspaces/${wsId}`, { name: wsName.trim() || current.name, color: wsColor, description: wsDesc });
      setWorkspaces(workspaces.map(w => w.workspace_id === wsId ? { ...w, ...data } : w));
      toast.success("Workspace updated");
    } catch { toast.error("Failed to save workspace"); }
    setSavingWs(false);
  };

  const deleteWorkspace = async () => {
    const ok = await confirmDelete({
      title: `Delete "${current?.name}"?`,
      message: "All events, edges, and templates in this workspace will be permanently removed. This cannot be undone.",
      confirmLabel: "Delete workspace",
      checkboxLabel: "I understand this is permanent",
    });
    if (!ok) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/workspaces/${wsId}`);
      setWorkspaces(workspaces.filter(w => w.workspace_id !== wsId));
      window.dispatchEvent(new CustomEvent("crescendo:workspace-deleted", { detail: { wsId } }));
      onClose();
      toast.success("Workspace deleted");
    } catch { toast.error("Failed to delete workspace"); }
    setDeleteBusy(false);
  };

  const handleSaveAll = () => {
    saveWallpaper();
    saveDisplay();
    onClose();
  };

  return (
    <>
      {cropSrc && (
        <WallpaperCropper src={cropSrc} onConfirm={(cropped) => {
          setBgUrl(cropped);
          setCropSrc(null);
          setDim(0.35);
          // Apply immediately so user sees it before hitting Save
          _applyBg(cropped, 0.35, bgBlur);
        }} onCancel={() => setCropSrc(null)} />
      )}
      {importOpen && <CalendarImportModal open onClose={() => setImportOpen(false)} />}

      <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="workspace-settings-modal">
        <div className="bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A2A]">
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-[#5A90F5]" />
              <h3 className="font-display text-sm font-semibold">Workspace Settings</h3>
              {current && <span className="text-[10px] text-[#52525B] truncate max-w-[120px]">{current.name}</span>}
            </div>
            <button onClick={onClose} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]"><X className="w-4 h-4" /></button>
          </div>

          {/* Tab bar */}
          <div className="flex border-b border-[#1a1a1a] px-2 gap-1">
            {[["workspace","Workspace"],["calendar","Calendar"],["themes","Wallpaper & Themes"]].map(([id, label]) => (
              <button key={id} onClick={() => setActiveSettingsTab(id)}
                className={`text-[11px] px-3 py-2 -mb-px border-b-2 transition-colors ${activeSettingsTab === id ? "border-[#5A90F5] text-[#EDEDED]" : "border-transparent text-[#A1A1AA] hover:text-[#EDEDED]"}`}>
                {label}
              </button>
            ))}
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">


            {/* ── Workspace identity ── */}
            <section className={activeSettingsTab !== "workspace" ? "hidden" : ""}>
              <div className="meta-label mb-3">WORKSPACE</div>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] text-[#A1A1AA] block mb-1">Name</label>
                  <input value={wsName} onChange={e => setWsName(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 text-sm outline-none focus:border-[#5A90F5]" />
                </div>
                <div>
                  <label className="text-[10px] text-[#A1A1AA] block mb-1">Description</label>
                  <textarea value={wsDesc} onChange={e => setWsDesc(e.target.value)} rows={2}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 text-sm outline-none focus:border-[#5A90F5] resize-none" />
                </div>
                <div>
                  <label className="text-[10px] text-[#A1A1AA] block mb-2">Color</label>
                  <div className="flex flex-wrap gap-1.5">
                    {COLOR_PRESETS.map(c => (
                      <button key={c} onClick={() => setWsColor(c)}
                        className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                        style={{ background: c, borderColor: wsColor === c ? "#EDEDED" : "transparent" }}
                      />
                    ))}
                  </div>
                </div>
                <button onClick={saveWorkspaceIdentity} disabled={savingWs}
                  className="px-3 py-1.5 rounded bg-[#5A90F5] text-white text-xs font-medium disabled:opacity-60 hover:bg-[#4a80e5]">
                  {savingWs ? "Saving…" : "Save Workspace"}
                </button>
              </div>
            </section>

            {/* ── Display ── */}
            <section className={activeSettingsTab !== "calendar" ? "hidden" : ""}>
              <div className="meta-label mb-3">DISPLAY</div>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] text-[#A1A1AA] block mb-1">Default Column Zoom — {zoom.toFixed(2)}×</label>
                  <input type="range" min={0.5} max={3} step={0.25} value={zoom} onChange={e => setZoom(parseFloat(e.target.value))}
                  className="cresc-slider w-full"
                  style={{ '--cresc-slider-pct': `${(((zoom - 0.5) / 2.5) * 100).toFixed(1)}%` }} />
                </div>
              </div>
            </section>


            {/* ── Day Colors ── */}
            <section className={activeSettingsTab !== "calendar" ? "hidden" : ""}>
              <div className="meta-label mb-3">DAY COLORS</div>
              <div className="grid grid-cols-7 gap-1.5">
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(day => (
                  <div key={day} className="flex flex-col items-center gap-1">
                    <span className="text-[9px] text-[#52525B] uppercase">{day}</span>
                    <label className="relative w-7 h-7 rounded cursor-pointer overflow-hidden border border-[#2A2A2A] hover:border-[#3A3A3A]"
                      style={{ background: weekdayTints?.[day] || "#141414" }}>
                      <input type="color"
                        value={weekdayTints?.[day] || "#141414"}
                        onChange={e => setWeekdayTints(t => ({ ...t, [day]: e.target.value }))}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
                    </label>
                    {weekdayTints?.[day] && (
                      <button onClick={() => setWeekdayTints(t => ({ ...t, [day]: null }))}
                        className="text-[8px] text-[#52525B] hover:text-[#A1A1AA]">✕</button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-[#52525B] mt-2">Click a day to set its column tint. Click ✕ to clear.</p>
            </section>

            {/* ── Day Header Format ── */}
            <section className={activeSettingsTab !== "calendar" ? "hidden" : ""}>
              <div className="meta-label mb-3">DAY HEADER FORMAT</div>
              <DayHeaderEditor />
            </section>

            {/* ── Time ── */}
            <section>
              <div className="meta-label mb-3">TIME & LOCALE</div>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] text-[#A1A1AA] block mb-1">Clock Format</label>
                  <div className="flex gap-2">
                    {[["12h","12-hour (3:00 PM)"],["24h","24-hour (15:00)"]].map(([k, l]) => (
                      <button key={k} onClick={() => setClock(k)}
                        className={`flex-1 py-2 rounded border text-xs ${clock === k ? "border-[#5A90F5] bg-[#5A90F5]/10 text-[#5A90F5]" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#3A3A3A]"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-[#A1A1AA] block mb-1">Week Starts On</label>
                  <div className="flex gap-2">
                    {[{v:0,l:"Sunday"},{v:1,l:"Monday"},{v:6,l:"Saturday"}].map(o => (
                      <button key={o.v} onClick={() => setWeekStartsOn(o.v)}
                        className={`flex-1 py-2 rounded border text-xs ${weekStartsOn === o.v ? "border-[#5A90F5] bg-[#5A90F5]/10 text-[#5A90F5]" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#3A3A3A]"}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-[#A1A1AA] block mb-2">Workweek Hours</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={1} max={80} step={1}
                      value={weekHours}
                      onChange={e => setWeekHours(Number(e.target.value))}
                      className="cresc-slider flex-1"
                      style={{ '--cresc-slider-pct': `${(((weekHours - 1) / 79) * 100).toFixed(1)}%` }}
                    />
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={1} max={168} step={1}
                        value={weekHours}
                        onChange={e => {
                          const v = Math.max(1, Math.min(168, parseInt(e.target.value) || 40));
                          setWeekHours(v);
                        }}
                        className="w-14 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5] text-center"
                      />
                      <span className="text-[10px] text-[#A1A1AA]">hrs/week</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-[#52525B] mt-1">Used by the capacity bar to show your weekly workload.</p>
                </div>
                <div>
                  <label className="text-[10px] text-[#A1A1AA] block mb-1">Timezone</label>
                  <select value={timezone} onChange={e => setTimezone(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5]">
                    {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
              </div>
            </section>
            {/* ── Wallpaper & Themes ── */}
            <section className={activeSettingsTab !== "themes" ? "hidden" : ""}>
              <div className="meta-label mb-3">BACKGROUND</div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#3A3A3A] bg-[#0A0A0A] cursor-pointer hover:border-[#5A90F5] text-xs text-[#EDEDED]">
                    <ImageIcon className="w-3 h-3" /> Upload & Crop
                    <input type="file" accept="image/*" onChange={onPickImage} className="hidden" />
                  </label>
                  {bgUrl && (
                    <button onClick={() => {
                      setBgUrl("");
                      // Remove from localStorage so it doesn't reload on next visit
                      localStorage.removeItem("crescendo:calBgUrl");
                      localStorage.removeItem(wsKey(wsId, "bgUrl"));
                      _applyBg("", 0.80, 12, undefined, 0.25, 0);
                    }} className="px-3 py-1.5 rounded border border-[#3A3A3A] text-[#FF3B30] hover:bg-[#FF3B30]/10 text-xs flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Clear
                    </button>
                  )}
                </div>

                {recentBgs.length > 0 && (
                  <div>
                    <p className="text-[10px] text-[#52525B] mb-1.5">Presets</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {recentBgs.map((r, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            setBgUrl(r.url);
                            setDim(r.dim ?? dim);
                            setBgBlur(r.blur ?? bgBlur);
                            setSidebarDim(r.sidebarDim ?? sidebarDim);
                            setEnvBlur(r.envBlur ?? envBlur);
                            _applyBg(r.url, r.dim ?? dim, r.blur ?? bgBlur, undefined, r.sidebarDim ?? sidebarDim, r.envBlur ?? envBlur);
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            const updated = recentBgs.filter((_, ri) => ri !== i);
                            const key = `crescendo:u:${current?.user_id}:recentBgs`;
                            try { localStorage.setItem(key, JSON.stringify(updated)); } catch {}
                            setRecentBgs(updated);
                          }}
                          className={`relative h-12 rounded border overflow-hidden transition-colors ${bgUrl === r.url ? "border-[#FFFFFF] ring-1 ring-[#FFFFFF]" : "border-[#2A2A2A] hover:border-[#5A90F5]"}`}
                          title="Click to apply · Right-click to remove"
                          style={{ backgroundImage: `linear-gradient(rgba(0,0,0,${r.dim ?? 0.5}),rgba(0,0,0,${r.dim ?? 0.5})),url("${r.url}")`, backgroundSize: "cover", backgroundPosition: "center" }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <input type="url" placeholder="…or paste an image URL" value={bgUrl} onChange={e => setBgUrl(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5]" />
                {bgUrl && (
                  <div className="space-y-2">
                    <div className="relative h-24 rounded border border-[#2A2A2A] overflow-hidden"
                      style={{ backgroundImage: `linear-gradient(rgba(0,0,0,${dim}),rgba(0,0,0,${dim})),url("${bgUrl}")`, backgroundSize: "cover", backgroundPosition: "center" }}>
                      <div className="absolute inset-0 flex items-stretch p-1.5 gap-1.5">
                        <div className="w-1/5 rounded" style={{ background: `rgba(12,12,12,${sidebarDim})`, backdropFilter: `blur(${bgBlur}px)` }} />
                        <div className="flex-1 rounded" style={{ background: "rgba(10,10,10,0.1)" }} />
                        <div className="w-1/6 rounded" style={{ background: `rgba(12,12,12,${sidebarDim})`, backdropFilter: `blur(${bgBlur}px)` }} />
                      </div>
                      <div className="absolute bottom-1 w-full text-center text-[9px] text-white/40">Preview</div>
                    </div>
                    <div>
                      <label className="text-[10px] text-[#A1A1AA] block mb-1.5">Darkness — {Math.round(dim * 100)}%</label>
                      <input type="range" min={0} max={0.95} step={0.01} value={dim}
                        className="cresc-slider w-full"
                        style={{ '--cresc-slider-pct': `${((dim / 0.95) * 100).toFixed(1)}%` }}
                        onChange={e => { const v = parseFloat(e.target.value); setDim(v); _applyBg(bgUrl, v, bgBlur, undefined, sidebarDim, envBlur); }} />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#A1A1AA] block mb-1.5">Sidebar blur — {bgBlur}px</label>
                      <input type="range" min={0} max={40} step={1} value={bgBlur}
                        className="cresc-slider w-full"
                        style={{ '--cresc-slider-pct': `${((bgBlur / 40) * 100).toFixed(1)}%` }}
                        onChange={e => { const v = parseFloat(e.target.value); setBgBlur(v); _applyBg(bgUrl, dim, v, undefined, sidebarDim, envBlur); }} />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#A1A1AA] block mb-1.5">Environment blur — {envBlur}px</label>
                      <input type="range" min={0} max={20} step={1} value={envBlur}
                        className="cresc-slider w-full"
                        style={{ '--cresc-slider-pct': `${((envBlur / 20) * 100).toFixed(1)}%` }}
                        onChange={e => { const v = parseFloat(e.target.value); setEnvBlur(v); _applyBg(bgUrl, dim, bgBlur, undefined, sidebarDim, v); }} />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#A1A1AA] block mb-1.5">Sidebar opacity — {Math.round(sidebarDim * 100)}%</label>
                      <input type="range" min={0.05} max={1} step={0.05} value={sidebarDim}
                        className="cresc-slider w-full"
                        style={{ '--cresc-slider-pct': `${(sidebarDim * 100).toFixed(1)}%` }}
                        onChange={e => { const v = parseFloat(e.target.value); setSidebarDim(v); _applyBg(bgUrl, dim, bgBlur, undefined, v, envBlur); }} />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#A1A1AA] block mb-1.5">Apply to</label>
                      <div className="flex gap-2">
                        {[["everywhere","Everywhere"],["calendar","Calendar only"],["documents","Documents only"]].map(([v, l]) => (
                          <button key={v} onClick={() => setBgApplyTo(v)}
                            className={`flex-1 py-1.5 rounded border text-[10px] transition-colors ${bgApplyTo === v ? "border-[#5A90F5] bg-[#5A90F5]/10 text-[#5A90F5]" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#3A3A3A]"}`}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* ── Import — calendar tab only ── */}
            <section className={activeSettingsTab !== "calendar" ? "hidden" : ""}>
              <div className="meta-label mb-3">IMPORT</div>
              <button onClick={() => { setImportOpen(true); }}
                className="w-full py-2 rounded border border-[#2A2A2A] text-xs text-[#A1A1AA] hover:text-[#EDEDED] hover:border-[#5A90F5] flex items-center justify-center gap-2">
                <RefreshCw className="w-3.5 h-3.5" /> Import from Google / Outlook…
              </button>
            </section>

            {/* ── Danger zone — workspace tab only ── */}
            <section className={`border border-[#FF3B30]/20 rounded-lg p-4 bg-[#FF3B30]/5 ${activeSettingsTab !== "workspace" ? "hidden" : ""}`}>
              <div className="meta-label text-[#FF3B30] mb-2">DANGER ZONE</div>
              <p className="text-[11px] text-[#A1A1AA] mb-3">Permanently delete this workspace and all its events, templates, and settings. This cannot be undone.</p>
              <button onClick={deleteWorkspace} disabled={deleteBusy}
                className="px-4 py-2 rounded bg-[#FF3B30] text-white text-xs font-medium hover:bg-[#e62e24] disabled:opacity-60 flex items-center gap-2">
                <Trash2 className="w-3.5 h-3.5" /> {deleteBusy ? "Deleting…" : "Delete Workspace"}
              </button>
            </section>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#2A2A2A]">
            <button onClick={onClose} className="px-4 py-2 text-xs text-[#A1A1AA] hover:text-[#EDEDED]">Cancel</button>
            <button onClick={handleSaveAll} className="px-4 py-2 rounded bg-[#5A90F5] text-white text-xs font-semibold hover:bg-[#4a80e5]">Save & Close</button>
          </div>
        </div>
      </div>
    </>
  );
}
// Listen for quick-switch preset applications from CalendarView header
if (typeof window !== "undefined") {
  window.addEventListener("crescendo:apply-bg-preset", (e) => {
    const p = e.detail;
    if (p?.url) _applyBg(p.url, p.dim ?? 0.8, p.blur ?? 12, undefined, p.sidebarDim ?? 0.25, p.envBlur ?? 0);
  });
}


